import React, {
    Children,
    useEffect,
    useId,
    useMemo,
    useRef,
    useState,
    forwardRef,
    useImperativeHandle,
    useCallback
} from 'react'
import {Box, HStack, Button, Flex, useSlotRecipe, useChakraContext} from '@chakra-ui/react3'
import PropTypes from 'prop-types'
import {useIntl} from 'react-intl'
import useResizeObserver from '../../../../hooks/use-resize-observer'
import useCssVarValue from '../../../hooks/useCssVarValue'
import useAnimationFrameCallback from '../../../hooks/useAnimationFrameCallback'
import {ArrowLeftIcon, ArrowRightIcon} from '../../icons'
import {sliderSlotRecipe} from './index.recipe'
import useEffectEvent from '../../../../hooks/use-effect-event'

/**
 * Finds an element with layout (visible and measurable) by checking the element itself first,
 * then recursively searching children to find the widest visible element.
 * Used to calculate item width when item contains nested elements with display:contents.
 *
 * @param {HTMLElement|null} el - The element to search within
 * @returns {HTMLElement|null} The element if visible, otherwise the widest visible descendant, or null if none found
 *
 * Why recursive: When using display:contents, the element does not have layout
 * (offsetWidth = 0), so we need to find a descendant that does have layout.
 * checkVisibility() returns false for elements with display:none or display:contents.
 */
const getLayoutElement = (el) => {
    if (!el) return null
    if (!el.checkVisibility || el.checkVisibility()) return el
    // get the widest child that is visible
    return Array.from(el.children).reduce((widestChild, child) => {
        const layoutChild = getLayoutElement(child)
        if (!widestChild || layoutChild.offsetWidth > widestChild.offsetWidth) return layoutChild
        return widestChild
    }, null)
}

const Slider = forwardRef(function Slider(
    {
        children,
        variant,
        sliderLabel,
        id: idProp,
        onIndexChange,
        navigationButtonVariant,
        paginationVariant,
        itemsPerSlide: overridenItemsPerSlide
    },
    ref
) {
    const {formatMessage} = useIntl()
    // false by default as pagination and nav buttons do not work before hydration
    const [isScrollable, setIsScrollable] = useState(false)
    const count = Children.count(children)
    const recipe = useSlotRecipe({recipe: sliderSlotRecipe})
    const styles = useMemo(
        () => recipe({variant, isScrollable, paginationVariant}),
        [recipe, variant, isScrollable, paginationVariant]
    )
    const generatedId = useId()
    const id = idProp ?? generatedId

    // need to use state to force rerender to trigger the resize observer and css var value
    const [containerEl, setContainerEl] = useState(null)
    const firstSlideRef = useRef(null)

    const [itemsPerSlide = 1] = useCssVarValue('--items-per-slide', {
        element: containerEl || null,
        initialValue: 1,
        parser: (value) => (value === '' ? 1 : parseFloat(value))
    })

    const indicatorNumber = Math.ceil(count - itemsPerSlide + 1)
    const maxIndex = Math.max(0, indicatorNumber - 1)
    const [current, setCurrent] = useState(0)
    const canPrev = current > 0
    const canNext = current < maxIndex

    const [stepWidth, setStepWidth] = useState(0)
    const pendingScrollRef = useRef(null)
    // RAF callback ensures layout reads (getComputedStyle, offsetWidth) happen at optimal frame timing,
    // avoiding forced synchronous layout and batching multiple resize events into single read per frame
    const doUpdateStepWidth = useAnimationFrameCallback(() => {
        if (!containerEl) return 0
        const cs = window.getComputedStyle(containerEl)
        const raw = cs.columnGap || cs.gap || '0'
        const val = parseFloat(raw)
        const gapX = Number.isFinite(val) ? val : 0

        const slideW = getLayoutElement(firstSlideRef.current)?.offsetWidth || 0

        setStepWidth(slideW + gapX)
        setIsScrollable(containerEl.scrollWidth > containerEl.clientWidth)
    })
    useResizeObserver(containerEl, doUpdateStepWidth, {debounceMs: 50})
    // update step width when count changes:
    // - handles initial render without children
    // - handles change in scrollable
    useEffect(() => {
        if (count > 0) {
            doUpdateStepWidth()
        }
    }, [count, stepWidth, doUpdateStepWidth])

    const clampIndex = useCallback((i) => Math.max(0, Math.min(i, maxIndex)), [maxIndex])

    const scrollToIndex = (i, behavior = 'smooth') => {
        if (!containerEl || stepWidth <= 0) {
            pendingScrollRef.current = {index: i, behavior}
            return
        }

        const target = i * stepWidth
        containerEl.scrollTo({left: target, behavior})
        pendingScrollRef.current = null
    }

    const prev = useEffectEvent(() => {
        const i = clampIndex(current - 1)
        scrollToIndex(i, 'smooth')
    })

    const next = useEffectEvent(() => {
        const i = clampIndex(current + 1)
        scrollToIndex(i, 'smooth')
    })

    const setCurrentIndex = useEffectEvent((i, options) => {
        scrollToIndex(clampIndex(i), options?.behavior ?? 'smooth')
    })

    useImperativeHandle(
        ref,
        () => ({
            setCurrentIndex,
            prev,
            next
        }),
        [setCurrentIndex, prev, next]
    )

    // Scroll handler: RAF ensures optimal timing, syncs state with actual position
    const updateCurrentFromScroll = useAnimationFrameCallback(() => {
        if (stepWidth <= 0) return
        if (!containerEl) return

        const i = Math.round(containerEl.scrollLeft / stepWidth)
        const isAtScrollEnd =
            containerEl.scrollLeft >= containerEl.scrollWidth - containerEl.clientWidth
        const nextIndex = isAtScrollEnd
            ? indicatorNumber - 1
            : Math.max(0, Math.min(i, indicatorNumber - 1))

        setCurrent(nextIndex)
        onIndexChange?.(nextIndex)
    })

    // apply pending scroll request once layout is measured
    useEffect(() => {
        if (!containerEl || stepWidth <= 0 || !pendingScrollRef.current) return

        const {index, behavior} = pendingScrollRef.current
        const target = clampIndex(index) * stepWidth

        containerEl.scrollTo({left: target, behavior})
        pendingScrollRef.current = null
    }, [containerEl, stepWidth, clampIndex])

    // update current item from scroll event
    useEffect(() => {
        if (!containerEl) return

        containerEl.addEventListener('scroll', updateCurrentFromScroll, {passive: true})

        return () => {
            containerEl.removeEventListener('scroll', updateCurrentFromScroll)
        }
    }, [containerEl, updateCurrentFromScroll])

    // update current item when items count, items per slide or step width changes
    useEffect(() => {
        updateCurrentFromScroll()
    }, [count, itemsPerSlide, stepWidth, updateCurrentFromScroll])

    const {css} = useChakraContext()
    const rootCss = useMemo(
        () => [
            styles.sliderContainer,
            // add the items per slide override if available and pass through the css routine to handle responsive values
            overridenItemsPerSlide && css({'--items-per-slide': overridenItemsPerSlide})
        ],
        [styles.sliderContainer, css, overridenItemsPerSlide]
    )

    return (
        <Box
            role="region"
            data-testid="slider"
            id={'slider-' + id}
            aria-label={sliderLabel}
            css={rootCss}
        >
            {/* Controls */}
            <Flex {...styles.controlsContainer}>
                <Flex {...styles.buttonGroup}>
                    <Button
                        {...styles.navButton}
                        variant={navigationButtonVariant || styles.navButton?.variant}
                        aria-controls={'slider-' + id}
                        aria-label={formatMessage({
                            id: 'global.gallery.scroll_prev',
                            defaultMessage: 'Scroll previous'
                        })}
                        onClick={prev}
                        disabled={!canPrev}
                    >
                        <ArrowLeftIcon />
                    </Button>
                    {count > 0 && (
                        <Box
                            role="status"
                            // on mobile chrome the label doesn't update, although the button's disabled state does update
                            // so use key to force recreate of the element when current or count changes
                            key={`${current}/${count}`}
                            {...styles.cockpitPagination}
                        >
                            {`${String(current + 1).padStart(2, '0')} / ${String(count).padStart(
                                2,
                                '0'
                            )}`}
                        </Box>
                    )}
                    <Button
                        {...styles.navButton}
                        variant={navigationButtonVariant || styles.navButton?.variant}
                        aria-controls={'slider-' + id}
                        aria-label={formatMessage({
                            id: 'global.gallery.scroll_next',
                            defaultMessage: 'Scroll next'
                        })}
                        onClick={next}
                        disabled={!canNext}
                    >
                        <ArrowRightIcon />
                    </Button>
                </Flex>
                <HStack {...styles.pagination}>
                    {Array.from({length: indicatorNumber}).map((_, i) => (
                        <Box
                            key={i}
                            {...(i === current && {'data-current': true})}
                            h="1px"
                            w={{base: '16px', _current: '36px'}}
                            bg={{
                                base: 'background.actions.carousel-bar',
                                _current: 'background.actions.on-media.active'
                            }}
                            borderRadius="full"
                            transition="all 0.3s ease"
                        />
                    ))}
                </HStack>
            </Flex>

            <Flex
                ref={setContainerEl}
                className="leap-slides"
                tabIndex={-1}
                scrollSnapType="x mandatory"
                scrollBehavior="smooth"
                {...styles.scrollContainer}
            >
                {Children.map(children, (child, idx) => {
                    const isVisible = idx >= current && idx < current + itemsPerSlide
                    return (
                        <Box
                            key={idx}
                            id={id + '-' + idx}
                            ref={idx === 0 ? firstSlideRef : null}
                            data-testid="slide"
                            scrollSnapAlign="start"
                            scrollSnapStop="always"
                            role="group"
                            aria-label={formatMessage({
                                id: 'global.carousel',
                                defaultMessage: 'carousel'
                            })}
                            inert={!isVisible ? '' : undefined}
                            aria-hidden={!isVisible || undefined}
                            aria-labelledby={id + '-headline-' + idx}
                            {...styles.slide}
                        >
                            {child}
                        </Box>
                    )
                })}
            </Flex>
        </Box>
    )
})

Slider.propTypes = {
    children: PropTypes.node.isRequired,
    itemsPerSlide: PropTypes.oneOfType([PropTypes.number, PropTypes.string, PropTypes.object]),
    variant: PropTypes.string,
    sliderLabel: PropTypes.string,
    id: PropTypes.string,
    onIndexChange: PropTypes.func,
    navigationButtonVariant: PropTypes.string,
    paginationVariant: PropTypes.string
}

export default Slider
